import { useEffect, useState, useMemo } from 'react'
import { FormProvider, useForm, type FieldErrors, type Resolver } from 'react-hook-form'
import { useMediaQuery } from '@mui/material'

import { RowBox } from '@components/shared/RowBox'
import { Input } from '@components/shared/Input'
import { EditIcon } from '@assets/svgs/EditIcon'
import { PlusCircleIcon } from '@assets/svgs/PlusCircleIcon'
import { Select } from '@components/shared/Select'
import { emotionTheme } from '@styles/theme'
import { initialFormValues } from './form/initialFormValues'
import { addEditServiceModalSchema } from './form/validations'
import { setInitialValues } from './helpers/setInitialValues'
import { ReturnModal } from '@components/modals/ReturnModal'
import { WarningIcon } from '@assets/svgs/WarningIcon'
import { parseDayName } from '@utils/functions/parseDayName'
import { formattedDatePtBr } from '@utils/dates/formattedDatePtBr'
import { BaseModal2 } from '@components/modals/BaseModal2'
import { extractDateIso } from '@utils/dates/extractDateIso'
import { useClientModal } from '../../context'

import type { AddEditOtherServicesModalProps } from './types'
import type { AddEditServiceModalFormProps } from './form/types'
import type { DayValueProps } from '../../context/types'

import * as S from './styles'

export const AddEditServiceModal = ({
  data,
  open,
  clientData,
  competenceDate,
  weekSelected,
  weekDaysData,
  onClose,
  onSubmitForm,
  canEdit = true,
  ...rest
}: AddEditOtherServicesModalProps) => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)

  const {
    serviceGroupingData,
    isServiceGroupingLoading,
    getServiceGrouping,
    serviceDescriptionsData,
    isServiceDescriptionsLoading,
    getServiceDescriptions,
    isCreateNewServiceLoading
  } = useClientModal()

  const [openCancelDetailedSummaryModal, setOpenCancelDetailedSummaryModal] = useState(false)
  const [daysValues, setDaysValues] = useState<DayValueProps[]>([])

  const isEdit = !!data

  const methods = useForm<AddEditServiceModalFormProps>({
    defaultValues: initialFormValues,
    resolver: addEditServiceModalSchema as unknown as Resolver<AddEditServiceModalFormProps>
  })

  const dayLabels = useMemo(() => {
    const valorDias = data?.valorDias || weekDaysData || []

    if (!valorDias || valorDias.length === 0) {
      return ['Qtd. Segunda', 'Qtd. Terça', 'Qtd. Quarta', 'Qtd. Quinta', 'Qtd. Sexta', 'Qtd. Sábado', 'Qtd. Domingo']
    }

    const startIndex = ((weekSelected ?? 1) - 1) * 7
    const weekDays = valorDias.slice(startIndex, startIndex + 7)

    return weekDays.map((dayData) => {
      const dayName = parseDayName(dayData.dia || '') || ''
      const dayAbbrev = dayName ? dayName.substring(0, 3) : ''
      let dateShort = ''

      if (dayData.data) {
        const dateFormatted = formattedDatePtBr(dayData.data)
        const parts = dateFormatted.split('/')
        if (parts.length === 3) {
          dateShort = `${parts[0]}/${parts[1]}/${parts[2].slice(-2)}`
        }
      }

      return `Qtd. ${dayAbbrev} (${dateShort})`
    })
  }, [data, weekSelected, weekDaysData])

  useEffect(() => {
    if (!open || isEdit) return

    const valorDias = weekDaysData || []
    const startIndex = ((weekSelected ?? 1) - 1) * 7
    const selectedWeekDays = valorDias.slice(startIndex, startIndex + 7)

    console.log('Adicionar Serviço - datas carregadas', {
      weekSelected,
      dayLabels,
      weekDays: selectedWeekDays.map((dayData) => ({
        data: dayData.data,
        dia: dayData.dia,
        valor: dayData.valor
      }))
    })
  }, [open, isEdit, weekSelected, weekDaysData, dayLabels])

  const onError = (errors: FieldErrors<AddEditServiceModalFormProps>) => console.log({ errors })

  const onSubmit = (formData: AddEditServiceModalFormProps) => {
    if (!canEdit) return

    onSubmitForm(formData)
  }

  const handleTotalQuantityCalculation = (label: string, dayValue: string) => {
    const grossUnitValue = Number(methods.getValues('grossUnitValue') ?? 0)
    const tax = Number(methods.getValues('tax').toString().replace(',', '.') ?? 0)

    const days = [...daysValues]
    const isoDate = extractDateIso(label)
    const index = isoDate ? days.findIndex((item) => item.data.startsWith(isoDate)) : -1

    const dayKeys = ['day1', 'day2', 'day3', 'day4', 'day5', 'day6', 'day7'] as const

    let totalQuantity: number

    if (index !== -1) {
      const oldDayValue = Number(days[index]?.valor) || 0
      const currentTotalQuantity = Number(methods.getValues('totalQuantity')) || 0
      totalQuantity = currentTotalQuantity - oldDayValue + (Number(dayValue) || 0)
      days[index] = { ...days[index], valor: Number(dayValue) }
      setDaysValues(days)
    } else {
      totalQuantity = dayKeys.reduce((sum, key) => sum + (Number(methods.getValues(key)) || 0), 0)
    }

    methods.setValue('totalQuantity', totalQuantity.toString())
    methods.setValue('grossTotal', (totalQuantity * grossUnitValue).toFixed(2))
    methods.setValue('netTotal', (totalQuantity * grossUnitValue * (1 - tax / 100)).toFixed(2))
  }

  const handleSetDays = (label: string, value: string) => {
    const currentDays = methods.getValues('days') || []
    const existingIndex = currentDays.findIndex((day) => day.label === label)

    if (existingIndex !== -1) {
      const updatedDays = [...currentDays]
      updatedDays[existingIndex] = { label, value }
      methods.setValue('days', updatedDays)
    } else {
      methods.setValue('days', [...currentDays, { label, value }])
    }
  }

  const handleTotalClacByDescription = (grossUnitValue: number, tax: number) => {
    const totalQuantity = methods.getValues('totalQuantity')

    const grossTotal = grossUnitValue * Number(totalQuantity)

    const netTotal = grossTotal * (1 - tax / 100)

    methods.setValue('grossTotal', grossTotal.toFixed(2))
    methods.setValue('netTotal', netTotal.toFixed(2))
  }

  const handleSetDescriptionValues = (descriptionId: number) => {
    const description = serviceDescriptionsData.find((item) => Number(item.id) === descriptionId)

    if (description) {
      methods.setValue('descriptionName', description.description)
      methods.setValue('grossUnitValue', description.unitGrossValue)
      methods.setValue('tax', description.billingTaxPercentage.toFixed(2).replace('.', ','))

      handleTotalClacByDescription(Number(description.unitGrossValue), Number(description.billingTaxPercentage))
    }
  }

  const handleGetServiceDescriptions = (grouping: string) => {
    getServiceDescriptions({
      filiCodigo: clientData?.filiCodigo!,
      cdClienteNl: clientData?.cdClienteNl!,
      nmAgrupamentoServico: grouping,
      fatDtCompetencia: competenceDate
    })
  }

  const handleGetInitialApis = async () => {
    getServiceGrouping({
      filiCodigo: clientData?.filiCodigo!,
      cdClienteNl: clientData?.cdClienteNl!
    })
  }

  useEffect(() => {
    if (!isEdit && open) {
      !!dayLabels[0] && methods.setValue('day1', '')
      !!dayLabels[1] && methods.setValue('day2', '')
      !!dayLabels[2] && methods.setValue('day3', '')
      !!dayLabels[3] && methods.setValue('day4', '')
      !!dayLabels[4] && methods.setValue('day5', '')
      !!dayLabels[5] && methods.setValue('day6', '')
      !!dayLabels[6] && methods.setValue('day7', '')
    }
  }, [open, dayLabels, isEdit])

  useEffect(() => {
    if (weekDaysData && weekDaysData.length > 0) {
      const initialDays = weekDaysData.map((day) => ({ ...day, valor: isEdit ? day.valor : 0 }))
      setDaysValues(initialDays)
    }
  }, [weekDaysData, isEdit, open])

  useEffect(() => {
    if (!!data && open) {
      methods.reset(setInitialValues(data!, weekSelected!) as unknown as AddEditServiceModalFormProps)
      handleGetServiceDescriptions(data?.agrupamentoServico || '')
    }
  }, [data, weekSelected, open])

  useEffect(() => {
    if (!open) {
      methods.reset(initialFormValues)
      onClose(false)
    } else {
      handleGetInitialApis()
    }
  }, [open])

  return (
    <>
      <BaseModal2
        open={open}
        width={isMobile ? 'calc(100% - 20px)' : 870}
        heightContainer="auto"
        maxHeightContainer={isMobile ? 'calc(100% - 20px)' : '80%'}
        title={isEdit ? 'Editar serviço' : 'Adicionar um serviço'}
        headerIcon={isEdit ? <EditIcon /> : <PlusCircleIcon />}
        onClose={() => setOpenCancelDetailedSummaryModal(true)}
        footer={{
          cancelButtonText: 'Cancelar',
          cancelButtonMinWidth: isMobile ? 'calc(50% - 8px)' : 193.5,
          confirmButtonText: canEdit ? 'Confirmar' : undefined,
          confirmButtonMinWidth: isMobile ? 'calc(50% - 8px)' : 193.5,
          onConfirm: canEdit ? () => methods.handleSubmit(onSubmit, onError)() : undefined,
          loading: isCreateNewServiceLoading
        }}
        {...rest}
      >
        <S.Title>
          {!isEdit
            ? 'Preencha todos os campos para adicionar um novo serviço.'
            : 'Preencha todos os campos antes de confirmar a edição do serviço.'}
        </S.Title>

        {isEdit && (
          <S.Warning>
            Atenção: Ao confirmar esta edição, todos os dados do serviço previamente registrados serão excluídos.
          </S.Warning>
        )}

        <FormProvider {...methods}>
          <S.Form style={{ marginTop: !isEdit ? 12 : 0 }} onSubmit={methods.handleSubmit(onSubmit, onError)}>
            <RowBox columns={2}>
              <Select
                required
                name="grouping"
                label="Agrupamento"
                placeholder="Selecione o agrup..."
                value={methods.watch('grouping')}
                options={
                  !!serviceGroupingData.length
                    ? serviceGroupingData.map((item) => ({
                        label: item.description,
                        value: item.description
                      }))
                    : []
                }
                onChange={({ target }) => {
                  handleGetServiceDescriptions(target.value as string)
                  methods.setValue('grouping', target.value as string)
                  methods.trigger('grouping')

                  methods.setValue('descriptionId', '')
                }}
                loading={isServiceGroupingLoading}
                disabled={!canEdit || isCreateNewServiceLoading || isServiceGroupingLoading}
              />

              <RowBox columns={2}>
                <Select
                  required
                  name="descriptionId"
                  label="Descrição"
                  placeholder="Selecione a desc..."
                  value={methods.watch('descriptionId')}
                  options={
                    !!serviceDescriptionsData.length
                      ? serviceDescriptionsData.map((item) => ({
                          label: item.description,
                          value: Number(item.id)
                        }))
                      : []
                  }
                  onChange={({ target }) => {
                    handleSetDescriptionValues(Number(target.value))
                    methods.setValue('descriptionId', target.value as string)
                    methods.trigger('descriptionId')
                  }}
                  loading={isServiceDescriptionsLoading}
                  disabled={!canEdit || !methods.watch('grouping') || isServiceDescriptionsLoading || isCreateNewServiceLoading}
                />

                <Input
                  name="grossUnitValue"
                  label="Valor bruto unitário"
                  maskType="currency"
                  placeholder="R$ 0,00"
                  disabled
                />
              </RowBox>
            </RowBox>

            <RowBox columns={4}>
              <Input name="tax" label="Imposto (%)" placeholder="0%" disabled />
              <Input required name="totalQuantity" label="Quantidade total" placeholder="0" disabled />
              <Input
                required
                name="grossTotal"
                label="Total Bruto (R$)"
                maskType="currency"
                placeholder="R$ 0,00"
                onBlur={({ target }) => methods.setValue('grossTotal', target.value)}
                disabled
              />
              <Input
                required
                name="netTotal"
                label="Total Líquido (R$)"
                maskType="currency"
                placeholder="R$ 0,00"
                onBlur={({ target }) => methods.setValue('netTotal', target.value)}
                disabled
              />
            </RowBox>

            <RowBox marginTop={8}>
              <S.Title>Informe a quantidade de refeições nos dias informados logo abaixo:</S.Title>
            </RowBox>

            <RowBox columns={4} marginTop={-16}>
              {!!dayLabels[0] && (
                <Input
                  required
                  name="day1"
                  label={dayLabels[0]}
                  placeholder="0"
                  onBlur={({ target }) => {
                    handleSetDays(dayLabels[0], target.value)
                    handleTotalQuantityCalculation(dayLabels[0], target.value)
                  }}
                  disabled={!canEdit || isCreateNewServiceLoading || !methods.watch('descriptionId')}
                />
              )}
              {!!dayLabels[1] && (
                <Input
                  required
                  name="day2"
                  label={dayLabels[1]}
                  placeholder="0"
                  onBlur={({ target }) => {
                    handleSetDays(dayLabels[1], target.value)
                    handleTotalQuantityCalculation(dayLabels[1], target.value)
                  }}
                  disabled={!canEdit || isCreateNewServiceLoading || !methods.watch('descriptionId')}
                />
              )}
              {!!dayLabels[2] && (
                <Input
                  required
                  name="day3"
                  label={dayLabels[2]}
                  placeholder="0"
                  onBlur={({ target }) => {
                    handleSetDays(dayLabels[2], target.value)
                    handleTotalQuantityCalculation(dayLabels[2], target.value)
                  }}
                  disabled={!canEdit || isCreateNewServiceLoading || !methods.watch('descriptionId')}
                />
              )}
              {!!dayLabels[3] && (
                <Input
                  required
                  name="day4"
                  label={dayLabels[3]}
                  placeholder="0"
                  onBlur={({ target }) => {
                    handleSetDays(dayLabels[3], target.value)
                    handleTotalQuantityCalculation(dayLabels[3], target.value)
                  }}
                  disabled={!canEdit || isCreateNewServiceLoading || !methods.watch('descriptionId')}
                />
              )}
              {!!dayLabels[4] && (
                <Input
                  required
                  name="day5"
                  label={dayLabels[4]}
                  placeholder="0"
                  onBlur={({ target }) => {
                    handleSetDays(dayLabels[4], target.value)
                    handleTotalQuantityCalculation(dayLabels[4], target.value)
                  }}
                  disabled={!canEdit || isCreateNewServiceLoading || !methods.watch('descriptionId')}
                />
              )}
              {!!dayLabels[5] && (
                <Input
                  required
                  name="day6"
                  label={dayLabels[5]}
                  placeholder="0"
                  onBlur={({ target }) => {
                    handleSetDays(dayLabels[5], target.value)
                    handleTotalQuantityCalculation(dayLabels[5], target.value)
                  }}
                  disabled={!canEdit || isCreateNewServiceLoading || !methods.watch('descriptionId')}
                />
              )}
              {!!dayLabels[6] && (
                <Input
                  required
                  name="day7"
                  label={dayLabels[6]}
                  placeholder="0"
                  onBlur={({ target }) => {
                    handleSetDays(dayLabels[6], target.value)
                    handleTotalQuantityCalculation(dayLabels[6], target.value)
                  }}
                  disabled={!canEdit || isCreateNewServiceLoading || !methods.watch('descriptionId')}
                />
              )}
            </RowBox>
          </S.Form>
        </FormProvider>
      </BaseModal2>

      <ReturnModal
        open={openCancelDetailedSummaryModal}
        headerIcon={<WarningIcon />}
        title="Deseja realmente cancelar a ação?"
        message="Todo o seu progresso será perdido!"
        headerTitle="Você tem certeza?"
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenCancelDetailedSummaryModal}
        onConfirm={() => {
          onClose(false)
          setOpenCancelDetailedSummaryModal(false)
        }}
      />
    </>
  )
}
